import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Button, Form, message, Modal, DatePicker, Space, Select } from "antd";
import { FilePdfOutlined, LinkOutlined } from "@ant-design/icons";
import moment from "moment";
import { generateSchedulePreviewLink } from "../../api/campaignsApi";
import BuildingUnitSelect from "../Buildings/BuildingUnitSelect";

const PDFPreviewDownload = ({ campaign }) => {
  const { t } = useTranslation();
  const [isModalOpen, setModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedCity, setSelectedCity] = useState(null);
  const [form] = Form.useForm();

  const handleCityChange = (value) => {
    setSelectedCity(value);
    // Reset building unit when city changes
    form.setFieldValue("building_unit_id", undefined);
  };

  const handleGenerateLink = async (values) => {
    setIsLoading(true);
    try {
      const data = {
        city_branch_ids: selectedCity ? [selectedCity] : undefined,
        building_unit_id: values.building_unit_id || undefined,
        date_from: values.date_from ? values.date_from.format("YYYY-MM-DD") : undefined,
        date_to: values.date_to ? values.date_to.format("YYYY-MM-DD") : undefined,
      };

      const response = await generateSchedulePreviewLink(data);
      
      // Open preview URL in new tab
      window.open(response.preview_url, "_blank");
      
      message.success(t("campaigns.previewLinkGenerated"));
      setModalOpen(false);
      form.resetFields();
      setSelectedCity(null);
    } catch (error) {
      console.error("API Error:", error);
      if (error.response?.status === 404) {
        message.error(t("campaigns.noBuildingUnitFound"));
      } else if (error.response?.status === 422) {
        message.error(error.response?.data?.error || t("campaigns.invalidParameters"));
      } else {
        message.error(t("campaigns.previewLinkError"));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenModal = () => {
    setModalOpen(true);
  };

  // Update form values when modal opens and campaign data is available
  useEffect(() => {
    if (isModalOpen && campaign) {
      const firstCityId = campaign.city_branches?.[0]?.id;
      setSelectedCity(firstCityId || null);
      
      // Pre-fill dates from campaign
      form.setFieldsValue({
        date_from: campaign.start_date ? moment(campaign.start_date) : undefined,
        date_to: campaign.end_date ? moment(campaign.end_date) : undefined,
      });
    }
  }, [isModalOpen, campaign, form]);

  return (
    <>
      <Button
        icon={<FilePdfOutlined />}
        onClick={handleOpenModal}
      >
        {t("campaigns.downloadSchedule")}
      </Button>

      <Modal
        title={t("campaigns.generateSchedulePreview")}
        open={isModalOpen}
        onCancel={() => {
          setModalOpen(false);
          form.resetFields();
          setSelectedCity(null);
        }}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleGenerateLink}
        >
          {/* Cities Selector */}
          <Form.Item
            label={t("campaigns.cities")}
            required
          >
            <Select
              mode="single"
              placeholder={t("campaigns.selectCity")}
              value={selectedCity}
              onChange={handleCityChange}
              options={campaign?.city_branches?.map(city => ({
                label: city.name,
                value: city.id,
              })) || []}
            />
          </Form.Item>

          {/* Building Unit Select - loads units for selected city */}
          <BuildingUnitSelect
            label={t("campaigns.buildingUnit")}
            name="building_unit_id"
            required={false}
            validationMessage={t("campaigns.buildingUnitRequired")}
            cityBranchId={selectedCity}
            mode="single"
          />

          {/* Date From - pre-filled from campaign */}
          <Form.Item
            name="date_from"
            label={t("campaigns.dateFrom")}
            tooltip={t("campaigns.datePrefilledFromCampaign")}
          >
            <DatePicker 
              style={{ width: "100%" }}
              format="DD.MM.YYYY"
            />
          </Form.Item>

          {/* Date To - pre-filled from campaign */}
          <Form.Item
            name="date_to"
            label={t("campaigns.dateTo")}
            tooltip={t("campaigns.datePrefilledFromCampaign")}
            dependencies={["date_from"]}
            rules={[
              {
                validator: (_, value) => {
                  const dateFrom = form.getFieldValue("date_from");
                  if (dateFrom && value && value.isBefore(dateFrom)) {
                    return Promise.reject(new Error(t("campaigns.dateToMustBeAfterDateFrom")));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <DatePicker 
              style={{ width: "100%" }}
              format="DD.MM.YYYY"
              disabledDate={(current) => {
                const dateFrom = form.getFieldValue("date_from");
                return dateFrom && current && current < dateFrom;
              }}
            />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button
                type="primary"
                icon={<LinkOutlined />}
                loading={isLoading}
                htmlType="submit"
              >
                {t("campaigns.generatePreviewLink")}
              </Button>
              <Button onClick={() => setModalOpen(false)}>
                {t("common.cancel")}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default PDFPreviewDownload;
